import { User } from "@/types/user";
import { Client } from "@/types/client";
import { Firm } from "@/types/firm";
import { Participant } from "@/types/meeting";

export type ConversationPlatform =
  | "ZOOM"
  | "MEET"
  | "TEAMS"
  | "INPERSON"
  | "PHONE"
  | "MOBILE"
  | "WEB"
  | "UPLOAD";

export type Keypoint = {
  noteId: string;
  note: Conversation;
  point: string;
};

export type TranscriptSegment = {
  id: string;
  content: string;
  speakerId: string;
  sentiment?: string;
  start?: number;
  orderId?: number;
};

export enum ConversationPurposeEntity {
  AllPurposeMeeting = "AllPurposeMeeting",
  SelfDictationMeeting = "SelfDictationMeeting",
  GeneralClientMeeting = "GeneralClientMeeting",
  IntroMeeting = "IntroMeeting",
}

export enum ConversationStatus {
  IN_PROGRESS = "IN_PROGRESS",
  PROCESSING_TRANSCRIPT = "PROCESSING_TRANSCRIPT",
  PROCESSING_SUMMARY = "PROCESSING_SUMMARY",
  DONE = "DONE",
  ERROR_TRANSCRIPT = "ERROR_TRANSCRIPT",
  ERROR_SUMMARY = "ERROR_SUMMARY",
  ERROR_UNKNOWN = "ERROR_UNKNOWN",
}

export enum DataCategory {
  GOALS = "GOALS",
  BUDGET = "BUDGET",
  INVESTMENTS = "INVESTMENTS",
  LIABILITIES = "LIABILITIES",
  INSURANCE = "INSURANCE",
  TAXES = "TAXES",
  ESTATE = "ESTATE",
  RISK_PROFILE = "RISK_PROFILE",
  INCOME = "INCOME",
  DEMOGRAPHICS = "DEMOGRAPHICS",
  INVESTMENT_PREFERENCES = "INVESTMENT_PREFERENCES",
  TIMEFRAME = "TIMEFRAME",
  ACCREDITATIONS = "ACCREDITATIONS",
}

export enum ConfidenceLevel {
  LOW = "LOW",
  MEDIUM = "MEDIUM",
  HIGH = "HIGH",
}

export enum DataExtractionStatus {
  PENDING = "PENDING",
  PROCESSING = "PROCESSING",
  COMPLETED = "COMPLETED",
  ERROR = "ERROR",
}

export interface DataExtractionResult {
  id: string;
  conversationId: string;
  status: DataExtractionStatus;
  createdAt: number;
  updatedAt: number;
}

export interface ConversationExtractedEntity {
  id: string;
  entityType: string;
  entityValue: Record<string, unknown>;
  createdAt: number;
  updatedAt: number;
}

export interface ConversationDataTableRowEntity {
  id: string;
  firmId: string;
  conversationId: string;
  name: string;
  category: DataCategory;
  value: string;
  referenceText: string;
  confidenceScore: ConfidenceLevel;
  clientId?: string | null;
  createdAt: number;
  updatedAt: number;
  entity?: ConversationExtractedEntity;
  entityId?: string;
  orderId?: number;
}

export enum ProcessingStepType {
  TRANSCRIPTION = "TRANSCRIPTION",
  SUMMARY = "SUMMARY",
  SUGGESTED_PARTICIPANTS = "SUGGESTED_PARTICIPANTS",
  COACHING = "COACHING",
  DATA_EXTRACTION = "DATA_EXTRACTION",
  SENTIMENT = "SENTIMENT",
}

export enum ProcessingStepStatus {
  WAITING = "WAITING",
  INPROGRESS = "INPROGRESS",
  COMPLETED = "COMPLETED",
  ERRORED = "ERRORED",
  NOT_APPLICABLE = "NOT_APPLICABLE",
}

export const TERMINAL_STEP_STATUSES: ProcessingStepStatus[] = [
  ProcessingStepStatus.COMPLETED,
  ProcessingStepStatus.ERRORED,
  ProcessingStepStatus.NOT_APPLICABLE,
];

export interface ProcessingStep {
  stepType: ProcessingStepType;
  status: ProcessingStepStatus;
  errorReason: string | null;
}

export type Conversation = {
  id: string;
  userId: string;
  user: User;
  clientId?: string | null;
  client?: Client | null;
  firmId: string;
  firm: Firm;
  title: string;
  original: string;
  transcript: TranscriptSegment[];
  summary?: string;
  keypoints: Keypoint[];
  recordedAt: number | string | null;
  createdAt: number;
  updatedAt: number;
  deletedAt?: number | null;
  participants?: Participant[];
  status?: ConversationStatus;
  purpose?: ConversationPurposeEntity;
  /** Latest custom summary attempt; null for standard or legacy summaries. */
  summaryTemplate?: {
    id?: string;
    name: string | null;
    status: ProcessingStepStatus;
  } | null;
  dataTableRows?: ConversationDataTableRowEntity[];
  platform?: ConversationPlatform;
  processingSteps?: ProcessingStep[] | null;
};

export interface SummarySection {
  section: string;
  content: string;
}

export interface SuggestedParticipant {
  clientId?: string;
  userId?: string;
}

export type SuggestedParticipants = SuggestedParticipant[];

export interface MergeSpeakersRequest {
  sourceSpeakerId: string;
  targetSpeakerId: string;
}
